import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import {
  AuthenticationActions,
  EAuthenticationActions,
  GetMetadataSuccess,
  GetMaintenanceWindowStatusSuccess,
  GetAuthenticationDataSuccess,
  GetMultipleOptionsSuccess,
  SaveAuthenticationData,
  UpdateAuthenticationDataSuccess,
  UpdateAuthenticationDataError,
  GetAuthenticationData,
  SetCurrentAuthenticator,
  SetMultipleViewMode,
  GetMultipleOptionsDefaultValuesSuccess,
  GetAuthenticationIsValidSuccess
} from '../actions/authentication.actions';
import { AuthenticationService } from 'app/security/core/services/authentication.service';
import { switchMap, withLatestFrom, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { BzgToastService } from 'bz-genome-angular';
import { TranslateService } from '@ngx-translate/core';
import { Store, select } from '@ngrx/store';
import { getAuthenticationData } from '../selectors/authentication.selectors';
import { EMultipleAuthViewMode } from '../../core/security-authentication.enum';
import { AuthenticationTypeValidationSevice } from 'app/shared/services/authentication-type-validation.service';
import { IAppState } from 'app/IAppState';

@Injectable()
export class AuthenticationEffects {

  getMetadataAuth$ = createEffect(() =>
    this._actions$.pipe(
      ofType<AuthenticationActions>(EAuthenticationActions.GetMetadata),
      switchMap(() => this._authenticationService.getAuthenticationMetadata()),
      switchMap((metatada: { [key: string]: any }) => {
        const isCloudFedramp = metatada['isCloudFedramp'];
        delete metatada['isCloudFedramp']; //this property is not needed in the state, it is only used to determine if the Multiple option should be available or not

        if (isCloudFedramp === false) {
          metatada['Multiple'] = { options: {} };
        }
        return of(new GetMetadataSuccess(metatada));
      })
    )
  );
  etMultipleOptions$ = createEffect(() =>
    this._actions$.pipe(
      ofType<AuthenticationActions>(EAuthenticationActions.GetMultipleOptions),
      switchMap(() => this._authenticationService.getMultipleOptions()),
      switchMap((metadata: Array<any>) => {
        return of(new GetMultipleOptionsSuccess(metadata));
      })
    )
  );
  getAuthenticationIsValid$ = createEffect(() =>
    this._actions$.pipe(
      ofType<AuthenticationActions>(EAuthenticationActions.GetAuthenticationIsValid),
      switchMap(() => this._authenticationService.getAuthenticationIsValid()),
      switchMap((isValid: boolean) => {
        this._authenticationTypeValidationService.validate(isValid);
        return of(new GetAuthenticationIsValidSuccess(isValid));
      })
    )
  );
  getMaintenanceStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType<AuthenticationActions>(
        EAuthenticationActions.GetMaintenanceWindowStatus
      ),
      switchMap(() => this._authenticationService.isAvailableForChanges()),
      switchMap((status: boolean) => {
        return of(new GetMaintenanceWindowStatusSuccess(status));
      })
    )
  );
  getAuthenticationData$ = createEffect(() =>
    this._actions$.pipe(
      ofType<AuthenticationActions>(EAuthenticationActions.GetAuthenticationData),
      switchMap(() => this._authenticationService.getAuthenticationData()),
      switchMap(data => {
        data[0].content.methods = JSON.parse(data[0].content.methods);
        data[0].content.multipleMethod = data[0].content.multipleMethod
          ? JSON.parse(data[0].content.multipleMethod)
          : this.getEmptyMultipleAuthentication();
        data[0].content.methods.push({ options: {}, type: 'Multiple' });
        return of(new GetAuthenticationDataSuccess(data))
      })
    )
  );
  SaveAuthenticationData$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SaveAuthenticationData>(EAuthenticationActions.SaveAuthenticationData),
      withLatestFrom(this._store.pipe(select(getAuthenticationData))),
      map(([action, authenticationData]) => authenticationData),
      switchMap((payload) =>
        this._authenticationService.putAuthentication(JSON.stringify(payload)).pipe(
          switchMap(() => {
            this.showSuccessToast();
            return [new UpdateAuthenticationDataSuccess(), new GetAuthenticationData()]
          }),
          catchError((error) => {
            return [new UpdateAuthenticationDataError(),
            new GetAuthenticationData(),
            new SetMultipleViewMode(EMultipleAuthViewMode.Unselect),
            new SetCurrentAuthenticator(null)]
          })
        )
      )
    )
  );
  getMultipleOptionsDefaultValues$ = createEffect(() =>
    this._actions$.pipe(
      ofType<AuthenticationActions>(EAuthenticationActions.GetMultipleOptionsDefaultValues),
      switchMap(() => this._authenticationService.getMultipleOptionsDefaultValues()),
      switchMap((defaults: any) => {
        return of(new GetMultipleOptionsDefaultValuesSuccess(defaults));
      })
    )
  );


  constructor(
    private _authenticationService: AuthenticationService,
    private _authenticationTypeValidationService: AuthenticationTypeValidationSevice,
    private _actions$: Actions,
    private _toastService: BzgToastService,
    private _translateService: TranslateService,
    private _store: Store<IAppState>
  ) {
  }

  private showSuccessToast() {
    this._translateService.get('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY').subscribe((message: string) => {
      this._toastService.open(
        message,
        {
          type: 'success',
          duration: 5000,
          verticalPosition: 'top',
          horizontalPosition: 'right'
        });
    });
  }


  private getEmptyMultipleAuthentication() {
    return {
      type: 'Multiple',
      authenticators: []
    }
  }
}
